import { useRef, useEffect, useMemo } from 'react';
import type { MediaItemComponent } from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';
import useIntersectionObserver from '@fogia/core-web/hooks/useIntersectionObserver';
import {
    inBrowser,
    isSupportingLazy,
    SANITY_IMAGE_PLACEHOLDER_WIDTH,
    SANITY_IMAGE_SRCSET_WIDTHS,
} from '@fogia/core-web/constants';
import { generateSanityImageUrl, generateSanityImageSrcSet, calculateObjectPosition } from './utils';

export interface UseSanityImageOptions {
    image: NonNullable<MediaItemComponent['image']>;
    loading?: 'lazy' | 'eager' | 'auto';
    /** Widths (px) for Sanity CDN srcSet. Defaults to SANITY_IMAGE_SRCSET_WIDTHS when omitted. */
    srcSetWidths?: readonly number[];
    /** sizes attribute for <img> (e.g. "100vw" or "(min-width: 48em) 50vw, 100vw") */
    sizes?: string;
    placeholder?: string | boolean;
    onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
    transitionDuration?: string;
}

/**
 * Return values from useSanityImage hook
 */
export interface UseSanityImageReturn {
    imageRef: React.RefObject<HTMLImageElement>;
    imageUrl: string | null;
    srcSet: string | null;
    placeholderUrl: string | undefined;
    objectPosition: string;
    imgProps: {
        'data-src'?: string;
        'data-srcset'?: string;
        'data-sizes'?: string;
        src?: string;
        srcSet?: string;
        sizes?: string;
        ref?: React.RefObject<HTMLImageElement>;
    };
    criticalStyle: React.CSSProperties;
    handleLoad: (event: React.SyntheticEvent<HTMLImageElement>) => void;
    isVisible: boolean;
    isCritical: boolean;
    shouldShowPlaceholder: boolean;
}

export const useSanityImage = ({
    image,
    loading = 'lazy',
    srcSetWidths,
    sizes,
    placeholder,
    onLoad,
    transitionDuration = '150ms',
}: UseSanityImageOptions): UseSanityImageReturn => {
    const imageRef = useRef<HTMLImageElement>(null);
    const widths = srcSetWidths ?? SANITY_IMAGE_SRCSET_WIDTHS;

    const { srcSet, imageUrl } = useMemo(() => {
        const generatedSrcSet = generateSanityImageSrcSet(image, widths);
        const fallbackWidth = widths.length > 0 ? widths[widths.length - 1] : undefined;
        const generatedImageUrl = fallbackWidth != null ? generateSanityImageUrl(image, fallbackWidth) : null;
        return { srcSet: generatedSrcSet, imageUrl: generatedImageUrl };
    }, [image, widths]);

    const isCritical = loading === 'eager';
    const useIOSupport = !isCritical && !isSupportingLazy;
    const isVisible = isCritical || inBrowser;

    // Generate placeholder (small blurred version)
    const placeholderUrl = useMemo(
        () =>
            typeof placeholder === 'string'
                ? placeholder
                : placeholder !== false && imageUrl
                  ? generateSanityImageUrl(image, SANITY_IMAGE_PLACEHOLDER_WIDTH) || undefined
                  : undefined,
        [placeholder, imageUrl, image],
    );

    const [observe, unobserve] = useIntersectionObserver(
        (entry) => {
            if (entry.intersectionRatio > 0 || entry.isIntersecting) {
                const img = imageRef.current;
                if (!img) return;

                // Set srcset and sizes first so the browser picks the right srcset candidate before loading src
                if (img.dataset.srcset != null) {
                    img.srcset = img.dataset.srcset;
                    delete img.dataset.srcset;
                }
                if (img.dataset.sizes != null) {
                    img.sizes = img.dataset.sizes;
                    delete img.dataset.sizes;
                }
                if (img.dataset.src && img.src !== img.dataset.src) {
                    img.src = img.dataset.src;
                }

                requestAnimationFrame(() => {
                    if (img.dataset.src) {
                        delete img.dataset.src;
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Reason: unobserve is a function that takes no arguments, but TypeScript infers it incorrectly
                    // @ts-ignore
                    unobserve();
                });
            }
        },
        { rootMargin: '200px' },
    );

    useEffect(() => {
        if (useIOSupport && imageRef.current) {
            return observe(imageRef.current);
        }
        return () => {};
    }, [observe, useIOSupport]);

    const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        if (!isCritical) {
            img.style.opacity = '1';
            const placeholderEl = img.previousElementSibling as HTMLElement;
            if (placeholderEl) {
                placeholderEl.style.backgroundImage = '';
            }
        }
        if (onLoad) onLoad(event);
    };

    const objectPosition = calculateObjectPosition(image.hotspot as { x: number; y: number } | undefined);

    const imgProps = useIOSupport
        ? {
              'data-src': imageUrl || undefined,
              'data-srcset': srcSet || undefined,
              'data-sizes': sizes,
              ref: imageRef,
          }
        : {
              src: imageUrl || undefined,
              srcSet: srcSet || undefined,
              sizes,
          };

    const criticalStyle = isCritical
        ? {}
        : { opacity: 0, transition: `opacity ${transitionDuration} ease-in`, willChange: 'opacity' };

    const shouldShowPlaceholder = Boolean(placeholderUrl && !isCritical);

    return {
        imageRef,
        imageUrl,
        srcSet,
        placeholderUrl,
        objectPosition,
        imgProps,
        criticalStyle,
        handleLoad,
        isVisible,
        isCritical,
        shouldShowPlaceholder,
    };
};
